import { Link } from "@tanstack/react-router";
import { CheckCircle, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabaseClient } from "@/lib/supabase";
import { cn } from "@/lib/utils";

export function LoginForm({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setShowResendOption(false);
    setResendSuccess(false);

    try {
      const { error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      setError(errorMessage);

      // Check if the error is about email not being confirmed
      if (errorMessage.toLowerCase().includes("email not confirmed")) {
        setShowResendOption(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email.trim()) return;

    setIsResending(true);
    setResendSuccess(false);
    try {
      const { error } = await supabaseClient.auth.resend({
        type: "signup",
        email: email.trim(),
      });

      if (error) {
        // Show rate limit or other errors to the user
        if (error.message.includes("after") && error.message.includes("seconds")) {
          setError(error.message);
        } else {
          // For other errors, still show success for security
          setResendSuccess(true);
          setShowResendOption(false);
          setError(null);
        }
        return;
      }

      setResendSuccess(true);
      setShowResendOption(false);
      setError(null);
    } catch (err) {
      // Handle unexpected errors - check for rate limit in catch as well
      const errorMessage = err instanceof Error ? err.message : "";
      if (errorMessage.includes("after") && errorMessage.includes("seconds")) {
        setError(errorMessage);
      } else {
        // Still show success for security (don't reveal if email exists)
        setResendSuccess(true);
        setShowResendOption(false);
        setError(null);
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription>Enter your email below to login to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin}>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  >
                    Forgot your password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              {showResendOption && (
                <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
                  <p className="text-sm text-muted-foreground">Need a new confirmation email?</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResendConfirmation}
                    disabled={isResending}
                    className="w-full"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Resend Confirmation Email
                      </>
                    )}
                  </Button>
                </div>
              )}
              {resendSuccess && (
                <div className="rounded-md border border-chart-2/30 bg-chart-2/10 p-3 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-chart-2 mt-0.5 shrink-0" />
                  <p className="text-sm text-foreground">
                    If an account exists with that email, we've sent a new confirmation link. Please
                    check your inbox and spam folder.
                  </p>
                </div>
              )}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Logging in..." : "Login"}
              </Button>
            </div>
            <div className="mt-4 text-center text-sm">
              Don&apos;t have an account?{" "}
              <Link to="/sign-up" className="underline underline-offset-4">
                Sign up
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
